/**
 * TradingPrompt.tsx — WEB.4.UI.13 / R4 Bug 9
 *
 * Modal prompt when current seat lands on the Trading tile.
 * Slider-based UI: player chooses how many energy to trade (0..currentEnergy).
 *
 * Conversion ratio: 1 energy → (8 − era) tokens.
 *   Era 1 = 7T, Era 2 = 6T, Era 3 = 5T, Era 4 = 4T
 *
 * ONE-WAY: tokens cannot be converted back to energy.
 * Min trade = 0 (skip without slider is also fine via Skip button).
 * Max trade = current energy (may go to zero).
 *
 * "Confirm Trade" is disabled when N=0.
 * "Skip" always closes without trading.
 *
 * VISIBLE WHEN: tradingPromptOpen === true
 * TEST PATH: roll dice → land on Trading tile → TradingPrompt renders.
 */

import type React from "react";
import { useState } from "react";
import { useGameStore } from "../../store/game-store.js";
import type { SeatIndex } from "../../store/game-store.js";
import styles from "./TradingPrompt.module.css";

function tradeRatio(era: 1 | 2 | 3 | 4): number {
  return 8 - era; // 7 | 6 | 5 | 4
}

export function TradingPrompt(): React.ReactElement | null {
  const tradingPromptOpen = useGameStore((s) => s.tradingPromptOpen);
  // R11.2 — codex HIGH: gate behind board-walk so trading modal doesn't pop
  // before the token visibly arrives at the Trading tile (rollDice + teleport
  // both set this flag synchronously).
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const energy = useGameStore((s) => s.energy);
  const tokens = useGameStore((s) => s.tokens);
  const era = useGameStore((s) => s.era);
  const executeTrade = useGameStore((s) => s.executeTrade);
  const skipTrade = useGameStore((s) => s.skipTrade);
  // T-3 — "Offer Card Swap" one-shot (R16 Builder-BY)
  const offerTrade = useGameStore((s) => s.offerTrade);
  const tradeOfferedThisGame = useGameStore((s) => s.tradeOfferedThisGame);
  // R19-GC-2 — rejection reason shown below the "Offer Card Swap" button.
  const tradeOfferRejectionReason = useGameStore((s) => s.tradeOfferRejectionReason);
  const hands = useGameStore((s) => s.hands);
  const lostSeats = useGameStore((s) => s.lostSeats);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const tradeAlreadyUsed = tradeOfferedThisGame[currentSeat] ?? false;
  // R20-GE-8: lowered minimum from 2 to 1 — 1-for-1 swap is valid.
  // Show the button when drawer has >=1 card and at least one opponent has >=1 card.
  const drawerHandSize = hands[currentSeat]?.length ?? 0;
  // Present seats only (opp < numPlayers) — absent seats are not in the game.
  const hasEligibleOpponent = ([0, 1, 2, 3] as SeatIndex[]).some(
    (opp) =>
      opp !== currentSeat &&
      opp < numPlayers &&
      !lostSeats.has(opp) &&
      (hands[opp]?.length ?? 0) >= 1,
  );
  const canOfferTrade = !tradeAlreadyUsed && drawerHandSize >= 1 && hasEligibleOpponent;

  const [sliderValue, setSliderValue] = useState(0);

  if (!tradingPromptOpen) return null;
  if (boardAnimationActive) return null;

  const seatEnergy = energy[currentSeat] ?? 0;
  const seatTokens = tokens[currentSeat] ?? 0;
  const ratio = tradeRatio(era);

  // Clamp slider value to valid range on each render (energy may change)
  const maxTrade = seatEnergy;
  const n = Math.min(sliderValue, maxTrade);
  const tokensGained = n * ratio;
  const canTrade = n > 0;

  function handleSlider(e: React.ChangeEvent<HTMLInputElement>) {
    setSliderValue(Number(e.target.value));
  }

  function handleConfirm() {
    executeTrade(n);
    setSliderValue(0);
  }

  function handleSkip() {
    setSliderValue(0);
    skipTrade();
  }

  return (
    <div className={styles.overlay} data-testid="trading-prompt-overlay">
      <div className={styles.panel} data-testid="trading-prompt-panel">
        <h2 className={styles.title}>TRADING POST</h2>

        {/* Era rate label */}
        <p className={styles.eraRate} data-testid="trading-era-rate">
          Era {era} rate: 1 energy = {ratio} tokens
        </p>

        {/* Slider */}
        {maxTrade > 0 ? (
          <div className={styles.sliderRow}>
            <span className={styles.sliderLabel}>0</span>
            <input
              type="range"
              className={styles.slider}
              min={0}
              max={maxTrade}
              step={1}
              value={n}
              onChange={handleSlider}
              data-testid="trading-slider"
              aria-label="Energy to trade"
            />
            <span className={styles.sliderLabel}>{maxTrade}</span>
          </div>
        ) : (
          <p className={styles.noEnergy} data-testid="trading-no-energy">
            No energy to trade.
          </p>
        )}

        {/* Live preview */}
        <p className={styles.preview} data-testid="trading-preview">
          {n === 0 ? "Move slider to trade" : `Trade ${n} energy → ${tokensGained} tokens`}
        </p>

        {/* Current → After row */}
        <div className={styles.statsRow} data-testid="trading-stats-row">
          <span>
            ⚡ {seatEnergy} · 💰 {seatTokens}
          </span>
          <span className={styles.arrow}>→</span>
          <span>
            ⚡ {seatEnergy - n} · 💰 {seatTokens + tokensGained}
          </span>
        </div>

        {/* Footer buttons */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={handleConfirm}
            disabled={!canTrade}
            title={
              canTrade ? `Trade ${n} energy for ${tokensGained} tokens` : "Move slider to trade"
            }
            data-testid="trading-confirm"
          >
            ▶ TRADE
          </button>
          {/* T-3 — Offer Card Swap (R16 Builder-BY). One-shot per game per drawer. */}
          <button
            type="button"
            className={styles.skipBtn}
            onClick={() => offerTrade()}
            disabled={!canOfferTrade}
            title={
              tradeAlreadyUsed
                ? "Already used card swap this game"
                : !hasEligibleOpponent
                  ? "No eligible opponent (need at least 1 card in hand)"
                  : drawerHandSize < 1
                    ? "Need at least 1 card in hand to swap"
                    : "Offer a card swap with an opponent"
            }
            data-testid="trading-offer-swap"
          >
            Offer Card Swap
          </button>
          <button
            type="button"
            className={styles.skipBtn}
            onClick={handleSkip}
            data-testid="trading-skip"
          >
            Skip
          </button>
        </div>
        {/* R19-GC-2: inline rejection reason when offerTrade is silently blocked */}
        {tradeOfferRejectionReason !== null && (
          <p className={styles.rejectMsg} data-testid="trade-reject-msg">
            {tradeOfferRejectionReason}
          </p>
        )}
      </div>
    </div>
  );
}
